import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader, CLoader, SEO } from '../../components/common';
import { FiCheck } from 'react-icons/fi';
import {
  fetchSpecializationCategories,
  fetchExpertSpecializations,
  setExpertSpecializations,
  SpecializationCategory,
} from '../../api';

const GROUP_LABELS: Record<string, string> = {
  asset_class: 'Asset Classes',
  strategy: 'Strategies',
  planning: 'Financial Planning',
};

const ExpertSpecializations: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<Record<string, SpecializationCategory[]>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [savedMessage, setSavedMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cats, existing] = await Promise.all([
        fetchSpecializationCategories(),
        fetchExpertSpecializations(),
      ]);
      setCategories(cats);
      setSelected(new Set(existing.map((s) => s.id)));
    } catch (error) {
      console.error('Error loading specializations:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= 5) return prev;
        next.add(id);
      }
      return next;
    });
    setSavedMessage('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setExpertSpecializations(Array.from(selected));
      setSavedMessage('Specializations saved successfully');
      setTimeout(() => setSavedMessage(''), 3000);
    } catch (error) {
      console.error('Error saving specializations:', error);
      setSavedMessage('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.backgroundColor }}>
        <CLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: colors.backgroundColor }}>
      <SEO title="Specializations - Expert Settings" noIndex />
      <div className="p-4 lg:p-8">
        <div className="lg:max-w-2xl lg:mx-auto">
          <CHeader title="Specializations" />

          <CText type="r14" color={colors.grayScale500} className="mb-1">
            Select up to 5 areas you specialize in. This helps clients find you for the right expertise.
          </CText>
          <CText type="M12" color={colors.primary} className="mb-6">
            {selected.size}/5 selected
          </CText>

          {Object.entries(categories).map(([group, cats]) => (
            <div key={group} className="mb-6">
              <CText type="B14" color={colors.textColor} className="mb-3">
                {GROUP_LABELS[group] || group}
              </CText>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {cats.map((cat) => {
                  const isSelected = selected.has(cat.id);
                  const isDisabled = !isSelected && selected.size >= 5;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      disabled={isDisabled}
                      className="flex items-center gap-2 px-3 py-3 rounded-xl text-left transition-all"
                      style={{
                        backgroundColor: isSelected ? colors.primary + '15' : colors.cardBackground || colors.inputBackground,
                        border: `1.5px solid ${isSelected ? colors.primary : colors.borderColor}`,
                        opacity: isDisabled ? 0.4 : 1,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <div
                        className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                        style={{
                          backgroundColor: isSelected ? colors.primary : 'transparent',
                          border: `1.5px solid ${isSelected ? colors.primary : colors.grayScale400}`,
                        }}
                      >
                        {isSelected && React.createElement(FiCheck as any, { size: 12, color: '#fff' })}
                      </div>
                      <span
                        className="text-sm font-medium"
                        style={{ color: isSelected ? colors.primary : colors.textColor }}
                      >
                        {cat.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3.5 rounded-xl font-medium text-sm mt-4 transition-all"
            style={{
              backgroundColor: colors.primary,
              color: colors.white,
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save Specializations'}
          </button>

          {savedMessage && (
            <CText
              type="M12"
              color={savedMessage.includes('success') ? '#22c55e' : colors.alertColor}
              className="text-center mt-3"
            >
              {savedMessage}
            </CText>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpertSpecializations;
